import React, { useState, useEffect } from 'react';
import StudentInformationService from '../../lib/services/studentInformationService';
import './StudentInformationDashboard.css';

const StudentInformationDashboard = ({ onBack, onTabChange }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [studentService] = useState(new StudentInformationService('tn_01'));

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching student dashboard data...');
      
      const { data, error: fetchError } = await studentService.getStudentDashboardData();
      
      if (fetchError) {
        console.error('Error fetching dashboard data:', fetchError);
        setError(fetchError.message || 'Failed to fetch dashboard data');
        return;
      }
      
      if (!data) {
        console.error('No dashboard data returned');
        setError('No data available');
        return;
      }
      
      setDashboardData(data);
      console.log('Dashboard data loaded successfully:', data);
      
    } catch (err) {
      console.error('Unexpected error in fetchDashboardData:', err);
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = async (classId) => {
    try {
      setLoading(true);
      const { data, error } = await studentService.getStudentsByClass(classId);
      
      if (error) {
        console.error('Error fetching class students:', error);
        setError(error.message || 'Failed to fetch class students');
        return;
      }
      
      setSelectedClass(data);
      setActiveTab('class-details');
      
    } catch (err) {
      console.error('Error in handleClassSelect:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = async (studentId) => {
    try {
      setLoading(true);
      const { data, error } = await studentService.getStudentDetails(studentId);
      
      if (error) {
        console.error('Error fetching student details:', error);
        setError(error.message || 'Failed to fetch student details');
        return;
      }
      
      setSelectedStudent(data);
      setActiveTab('student-details');
      
    } catch (err) {
      console.error('Error in handleStudentSelect:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await studentService.searchStudents(query);
      
      if (error) {
        console.error('Error searching students:', error);
        setSearchResults([]);
        return;
      }
      
      setSearchResults(data || []);
      
    } catch (err) {
      console.error('Error in handleSearch:', err);
    }
  };

  const downloadClassStudentList = async (classData) => {
    try {
      if (!classData || !classData.students || classData.students.length === 0) {
        alert('沒有學生資料可供下載');
        return;
      }

      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;

      // Create HTML content with proper Chinese font support
      const exportDate = new Date().toLocaleDateString('en-HK');
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', 'Noto Sans CJK SC', sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #333;
            }
            .school-subtitle {
              font-size: 16px;
              margin-bottom: 10px;
              color: #666;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
            }
            .class-info {
              font-size: 14px;
              margin-bottom: 20px;
              text-align: left;
              color: #555;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              background: white;
            }
            th {
              background-color: #4285f4;
              color: white;
              padding: 8px;
              text-align: left;
              font-weight: bold;
              border: 1px solid #ddd;
              font-size: 11px;
            }
            td {
              padding: 6px 8px;
              border: 1px solid #ddd;
              text-align: left;
              font-size: 10px;
              color: #333;
            }
            tr:nth-child(even) {
              background-color: #f5f5f5;
            }
            tr:nth-child(odd) {
              background-color: white;
            }
            .footer {
              font-size: 10px;
              color: #666;
              text-align: center;
              margin-top: 20px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="school-name">MyClass</div>
            <div class="school-subtitle">Real School A</div>
            <div class="title">Class Student List</div>
            <div class="class-info">
              <div>Class: ${classData.classInfo?.name || classData.classId}</div>
              <div>Class Code: ${classData.classId}</div>
              <div>Export Date: ${exportDate}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th>No</th>
                <th>Student ID</th>
                <th>English Name</th>
                <th>Chinese Name</th>
              </tr>
            </thead>
            <tbody>
              ${classData.students.map((student, index) => `
                <tr>
                  <td>${classData.classInfo?.name || classData.classId}</td>
                  <td>${index + 1}</td>
                  <td>${student.student_id || 'N/A'}</td>
                  <td>${student.english_name || 'N/A'}</td>
                  <td>${student.name || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            Generated by MyClass Student Information Dashboard | Total Students: ${classData.students.length}
          </div>
        </body>
        </html>
      `;

      // Create a hidden iframe to properly render the HTML
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      // Write HTML content to iframe
      iframe.contentDocument.open();
      iframe.contentDocument.write(htmlContent);
      iframe.contentDocument.close();

      // Wait for iframe to load and render
      await new Promise((resolve) => {
        iframe.onload = resolve;
        setTimeout(resolve, 1000);
      });

      // Get the body element from iframe
      const element = iframe.contentDocument.body;

      // Configure PDF options
      const options = {
        margin: [10, 10, 10, 10],
        filename: `ClassStudentList_${classData.classId}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 794,
          height: 1123
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      };

      // Generate and download PDF
      await html2pdf().set(options).from(element).save();
      
      // Clean up
      document.body.removeChild(iframe);

      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading student list:', error);
      alert('下載學生名單時發生錯誤: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="student-info-dashboard">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>學生資訊</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-info-dashboard">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>學生資訊</h1>
        </div>
        <div className="error-container">
          <div className="error-message">
            <h3>載入錯誤</h3>
            <p>{typeof error === 'string' ? error : error?.message || 'Unknown error occurred'}</p>
            <button onClick={fetchDashboardData} className="retry-button">
              重試
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-info-dashboard">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>學生資訊</h1>
        <button className="refresh-button" onClick={fetchDashboardData} title="重新載入">
          🔄
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="student-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          學生概覽
        </button>
        <button 
          className={`tab-btn ${activeTab === 'class-browser' ? 'active' : ''}`}
          onClick={() => setActiveTab('class-browser')}
        >
          班級瀏覽
        </button>
        <button 
          className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          學生搜尋
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {activeTab === 'overview' && dashboardData && (
          <div className="overview-section">
            {/* Summary Statistics */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-number">{dashboardData.totalStudents}</div>
                  <div className="stat-label">總學生數</div>
                  <div className="stat-subtitle">全校學生</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🏫</div>
                <div className="stat-content">
                  <div className="stat-number">{dashboardData.totalClasses}</div>
                  <div className="stat-label">班級數量</div>
                  <div className="stat-subtitle">完整配置</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <div className="stat-number">{dashboardData.averageAttendanceRate}%</div>
                  <div className="stat-label">平均出席率</div>
                  <div className="stat-subtitle">本學期</div>
                </div>
              </div>
            </div>

            {/* Grade Distribution */}
            <div className="grade-distribution">
              <h2>年級分佈</h2>
              <div className="grade-grid">
                {dashboardData.formLevelStats.map((formLevel, index) => (
                  <div key={index} className="grade-card">
                    <div className="grade-number">{formLevel.studentCount}</div>
                    <div className="grade-label">{formLevel.formLevel}</div>
                    <div className="grade-subtitle">{formLevel.classCount}個班別</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'class-browser' && dashboardData && (
          <div className="class-browser-section">
            <h2>班級瀏覽</h2>
            <div className="class-grid">
              {dashboardData.formLevelStats.map((formLevel, index) => (
                <div key={index} className="form-level-group">
                  <h3>{formLevel.formLevel}</h3>
                  <div className="class-buttons">
                    {formLevel.classes.map((classInfo, classIndex) => (
                      <div key={classIndex} className="class-card">
                        <div className="class-header">
                          <h4 className="class-name">{classInfo.className}</h4>
                          <span className="student-count">{classInfo.studentCount} 學生</span>
                        </div>
                        
                        <div className="teacher-info">
                          <div className="teacher-row">
                            <span className="teacher-label">班主任:</span>
                            <span className="teacher-name">
                              {classInfo.teacherInfo?.classMaster?.name || '未安排'}
                            </span>
                            {classInfo.teacherInfo?.classMaster?.subject && (
                              <span className="teacher-subject">({classInfo.teacherInfo.classMaster.subject})</span>
                            )}
                          </div>
                          
                          <div className="teacher-row">
                            <span className="teacher-label">副班主任:</span>
                            <span className="teacher-name">
                              {classInfo.teacherInfo?.viceMaster?.name || '未安排'}
                            </span>
                            {classInfo.teacherInfo?.viceMaster?.subject && (
                              <span className="teacher-subject">({classInfo.teacherInfo.viceMaster.subject})</span>
                            )}
                          </div>
                          
                          {classInfo.teacherInfo?.subjectTeachers && classInfo.teacherInfo.subjectTeachers.length > 0 && (
                            <div className="teacher-row">
                              <span className="teacher-label">科任老師:</span>
                              <div className="subject-teachers">
                                {classInfo.teacherInfo.subjectTeachers.map((teacher, idx) => (
                                  <span key={idx} className="subject-teacher">
                                    {teacher.name} ({teacher.subjects})
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button
                          className="view-details-btn"
                          onClick={() => handleClassSelect(classInfo.classId)}
                        >
                          查看詳情
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="search-section">
            <h2>學生搜尋</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="輸入學生姓名或學號..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="search-input"
              />
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((student, index) => (
                    <div
                      key={index}
                      className="search-result-item"
                      onClick={() => handleStudentSelect(student.student_id)}
                    >
                      <div className="student-info">
                        <div className="student-name">{student.name}</div>
                        <div className="student-details">
                          {student.student_id} • {student.saas_classes?.name || student.class_id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'class-details' && selectedClass && (
          <div className="class-details-section">
            <div className="class-header">
              <button className="back-to-overview" onClick={() => setActiveTab('overview')}>
                ← 返回概覽
              </button>
              <h2>{selectedClass.classInfo?.name || selectedClass.classId}</h2>
              <button 
                className="download-button"
                onClick={() => downloadClassStudentList(selectedClass)}
              >
                📄 下載學生名單
              </button>
            </div>
            
            <div className="class-teachers">
              {selectedClass.classTeachers.map((teacher, index) => (
                <div key={index} className="teacher-info">
                  <span className="teacher-role">{teacher.class_master ? '班主任' : '副班主任'}:</span>
                  <span className="teacher-name">{teacher.name}</span>
                  <span className="teacher-subject">({teacher.subject_specialty})</span>
                </div>
              ))}
            </div>
            
            <div className="students-list">
              <h3>學生名單 ({selectedClass.studentCount})</h3>
              <div className="students-grid">
                {selectedClass.students.map((student, index) => (
                  <div
                    key={index}
                    className="student-card"
                    onClick={() => handleStudentSelect(student.student_id)}
                  >
                    <div className="student-number">{index + 1}</div>
                    <div className="student-info">
                      <div className="student-name">{student.name}</div>
                      <div className="student-id">{student.student_id}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'student-details' && selectedStudent && (
          <div className="student-details-section">
            <div className="student-header">
              <button className="back-to-overview" onClick={() => setActiveTab('overview')}>
                ← 返回概覽
              </button>
              <h2>{selectedStudent.student.name}</h2>
            </div>
            
            <div className="student-profile">
              <div className="profile-section">
                <h3>基本資料</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <label>學號:</label>
                    <span>{selectedStudent.student.student_id}</span>
                  </div>
                  <div className="profile-item">
                    <label>班別:</label>
                    <span>{selectedStudent.student.className}</span>
                  </div>
                  <div className="profile-item">
                    <label>年級:</label>
                    <span>{selectedStudent.student.formLevel}</span>
                  </div>
                  <div className="profile-item">
                    <label>電郵:</label>
                    <span>{selectedStudent.student.email || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div className="profile-section">
                <h3>學業表現</h3>
                <div className="academic-stats">
                  <div className="academic-item">
                    <label>平均分:</label>
                    <span>{selectedStudent.academic.averageGrade}</span>
                  </div>
                  <div className="academic-item">
                    <label>成績記錄:</label>
                    <span>{selectedStudent.academic.totalGrades} 項</span>
                  </div>
                </div>
              </div>
              
              <div className="profile-section">
                <h3>出席記錄</h3>
                <div className="attendance-stats">
                  <div className="attendance-item">
                    <label>出席率:</label>
                    <span>{selectedStudent.attendance.attendanceRate}%</span>
                  </div>
                  <div className="attendance-item">
                    <label>記錄數:</label>
                    <span>{selectedStudent.attendance.records.length} 項</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentInformationDashboard;
